import { Component, signal } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { ActivatedRoute } from '@angular/router';
import { UrlConstantsService } from 'src/app/services/url-constants.service';
import { versions } from 'src/app/versions';
import { passwordStrengthValidator } from '../password-rules';

function passwordsMatchValidator(group: AbstractControl): ValidationErrors | null {
  const { password, confirm } = group.value;
  return confirm && password !== confirm ? { mismatch: true } : null;
}

/** Page behind the link in the password reset email: /reset-password?uid=…&token=… */
@Component({
  selector: 'app-user-reset-password',
  templateUrl: './user-reset-password.component.html',
  styleUrls: ['./user-reset-password.component.scss'],
  standalone: false,
})
export class UserResetPasswordComponent {
  // Signals: the app does not repaint after async work on its own.
  readonly isLoading = signal(false);
  readonly state = signal<'form' | 'done' | 'invalid' | 'disabled'>('form');
  readonly passwordError = signal('');
  readonly formError = signal('');
  readonly showPassword = signal(false);
  readonly versions = versions;

  private readonly uid: string;
  private readonly token: string;

  form = new FormGroup(
    {
      password: new FormControl('', [
        Validators.required,
        Validators.minLength(5),
        Validators.maxLength(24),
        passwordStrengthValidator,
      ]),
      confirm: new FormControl('', Validators.required),
    },
    { validators: passwordsMatchValidator },
  );

  constructor(
    private http: HttpClient,
    private urls: UrlConstantsService,
    route: ActivatedRoute,
  ) {
    const params = route.snapshot.queryParamMap;
    this.uid = params.get('uid') ?? '';
    this.token = params.get('token') ?? '';
    if (!this.uid || !this.token) this.state.set('invalid');
  }

  has(name: 'password' | 'confirm', error: string): boolean {
    const control = this.form.get(name);
    return !!control && control.touched && control.hasError(error);
  }

  get passwordInvalid(): boolean {
    const control = this.form.get('password');
    return (!!control?.touched && control.invalid) || !!this.passwordError();
  }

  get mismatch(): boolean {
    return !!this.form.get('confirm')?.touched && this.form.hasError('mismatch');
  }

  submit(): void {
    this.form.markAllAsTouched();
    if (this.form.invalid || this.isLoading()) return;

    this.isLoading.set(true);
    this.passwordError.set('');
    this.formError.set('');
    const body = { uid: this.uid, token: this.token, new_password: this.form.value.password };
    this.http.post(this.urls.USER_RESET_PASSWORD, body).subscribe({
      next: () => {
        this.isLoading.set(false);
        this.state.set('done');
      },
      error: (err: any) => {
        this.isLoading.set(false);
        // The error interceptor may hand over the body itself or the full response.
        const errors = err?.error ?? err;
        if (errors?.code === 'invalid_link') {
          this.state.set('invalid');
        } else if (errors?.code === 'password_resets_disabled') {
          this.state.set('disabled');
        } else if (errors?.new_password?.length) {
          this.passwordError.set(errors.new_password[0]);
        } else {
          this.formError.set(errors?.detail ?? 'Something went wrong. Please try again.');
        }
      },
    });
  }
}
